import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  IconButton,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Tooltip,
  Alert,
  FormControlLabel,
  Switch,
  Stack,
  TableHead,
  LinearProgress,
} from '@mui/material';
import {
  PlayArrow as PlayArrowIcon,
  Stop as StopIcon,
  Settings as SettingsIcon,
  AutoFixHigh as AutoFixHighIcon,
  GraphicEq as GraphicEqIcon,
  CloudUpload as CloudUploadIcon,
  Upload as UploadIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { sheetsService } from '../services/googleSheetsService';
import { SettingsDialog } from './SettingsDialog';
import { ScriptManager } from './ScriptManager';
import { LimiterSettingsDialog } from './LimiterSettingsDialog';
import { styled } from '@mui/material/styles';
import { WaveformEditorDialog } from './WaveformEditorDialog';
import VUMeter from './VUMeter';
import { useSnackbar } from 'notistack';

// Hidden input for file upload
const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

// Define TranscriptionResult interface
interface TranscriptionResult {
  text: string;
  segments: Array<{
    start: number;
    end: number;
    text: string;
  }>;
  timestamps: {
    [key: string]: {
      inPoint: number;
      outPoint: number;
    }
  };
  fileUrl?: string;
  fileId?: string;
  words?: Array<{
    word: string;
    normalizedWord: string;
    startTime: number;
    endTime: number;
  }>;
  peaks?: number[];
}

// Helper function for text normalization (copied from server.js)
// const normalizeText = (text: string): string => { // Already commented out, ensure it remains so or is removed
//   if (!text) return '';
//   return text.normalize('NFKD') // Apply normalization first
//     .replace(/[\u0300-\u036f]/g, '') // Remove diacritics
//     .toLowerCase() // Convert to lowercase
//     .replace(/[.,!?;:"'-]/g, '') // Remove punctuation (hyphen doesn't need escape)
//     .replace(/\s+/g, ' ') // Normalize whitespace
//     .trim(); // Trim leading/trailing spaces
// };

// Cell mapping assumes VO data is in column L of the target sheet
const CELL_MAP: { [key: string]: string } = {
  '10': 'L2',
  '9': 'L3',
  '8': 'L4',
  '7': 'L5',
  '6': 'L6',
  '5': 'L7',
  '4': 'L8',
  '3': 'L9',
  '2': 'L10',
  '1': 'L11',
  'Intro': 'L12',
  'Outro': 'L13'
};

// Add mapping for VO In and VO Out columns
const VO_IN_MAP: { [key: string]: string } = {
  'Intro': 'J12',
  '10': 'J2',
  '9': 'J3',
  '8': 'J4',
  '7': 'J5',
  '6': 'J6',
  '5': 'J7',
  '4': 'J8',
  '3': 'J9',
  '2': 'J10',
  '1': 'J11',
  'Outro': 'J13'
};

const VO_OUT_MAP: { [key: string]: string } = {
  'Intro': 'K12',
  '10': 'K2',
  '9': 'K3',
  '8': 'K4',
  '7': 'K5',
  '6': 'K6',
  '5': 'K7',
  '4': 'K8',
  '3': 'K9',
  '2': 'K10',
  '1': 'K11',
  'Outro': 'K13'
};

// Define the desired display order
const DISPLAY_ORDER = [
  'Intro',
  '10', '9', '8', '7', '6', '5', '4', '3', '2', '1',
  'Outro'
];

interface VOManagerProps {
  spreadsheetId?: string;
}

// Add this constant near the top of the file with other constants
const DEFAULT_OUTRO_AUDIO_URL = `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/public/default-outro.wav`;
const DEFAULT_OUTRO_TEXT = "IF YOU WANT MORE BILLBOARD, MAKE SURE YOU HIT THE SUBSCRIBE BUTTON AND RING THE BELL TO BE NOTIFIED ON ALL OUR LATEST VIDEOS";

// Add this constant near the top where other constants are defined
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// We no longer need to track or write to Voice Over URL cell

export const VOManager: React.FC<VOManagerProps> = ({ spreadsheetId: propSpreadsheetId }) => {
  const navigate = useNavigate();
  const { spreadsheetId: routeSpreadsheetId } = useParams();
  const [activeSpreadsheetId, setActiveSpreadsheetId] = useState<string | null>(routeSpreadsheetId || propSpreadsheetId || null);
  const [voData, setVoData] = useState<{ [key: string]: string }>({});
  const [generatedAudio, setGeneratedAudio] = useState<{ [key: string]: string | null }>({});
  const [editingCell, setEditingCell] = useState<{ key: string; value: string } | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [isGeneratingAll, setIsGeneratingAll] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [playingKey, setPlayingKey] = useState<string | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const compressorNodeRef = useRef<DynamicsCompressorNode | null>(null);
  const gainNodeRef = useRef<GainNode | null>(null);
  const sourceNodeRef = useRef<MediaElementAudioSourceNode | null>(null);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [scriptDialogOpen, setScriptDialogOpen] = useState(false);
  const [voiceSettings, setVoiceSettings] = useState({
    voiceId: localStorage.getItem('voiceSettings_voiceId') || 'exkLoDgg9PFCos2rzsiW',
    stability: parseFloat(localStorage.getItem('voiceSettings_stability') || '0.33'),
    similarityBoost: parseFloat(localStorage.getItem('voiceSettings_similarityBoost') || '0.3'),
  });
  const [isGenerateMode, setIsGenerateMode] = useState<boolean>(() => {
    const savedMode = localStorage.getItem('voManagerMode');
    return savedMode ? savedMode === 'generate' : false; // Default to Upload VO mode
  });
  const [voInPoints, setVoInPoints] = useState<{ [key: string]: string }>({});
  const [voOutPoints, setVoOutPoints] = useState<{ [key: string]: string }>({});
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [limiterSettings, setLimiterSettings] = useState({
    gain: parseFloat(localStorage.getItem('limiterSettings_gain') || '0'), // This is the main makeup gain
  });
  const [showLimiterDialog, setShowLimiterDialog] = useState(false);
  const [columnMappings, setColumnMappings] = useState<{
    voInColLetter: string;
    voOutColLetter: string;
  }>({ voInColLetter: '', voOutColLetter: '' }); // Re-added state
  const [processedAudioUrl, setProcessedAudioUrl] = useState<string | null>(null);
  const audioElementRef = useRef<HTMLAudioElement | null>(null);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [isUploadSuccess, setIsUploadSuccess] = useState<boolean>(false);
  const [transcriptionResult, setTranscriptionResult] = useState<TranscriptionResult | null>(null);
  // Re-add peaks state
  const [processedAudioPeaks, setProcessedAudioPeaks] = useState<number[] | null>(null);

  // Add state to manage opening the editor after data is set
  const [pendingEditorKey, setPendingEditorKey] = useState<string | null>(null);

  // Add new state for the waveform editor
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  // Define state for waveform editor
  interface EditorData {
    lineKey: string;
    initialInPoint: number;
    initialOutPoint: number;
    initialGain?: number; // Add initialGain to the interface
    audioUrl: string | null;
    songTitle?: string;
    songArtist?: string;
  }
  const [editorData, setEditorData] = useState<EditorData | null>(null);

  // Add new state to store detected column letters
  const [detectedColumns, setDetectedColumns] = useState<{
    voInCol: string;
    voOutCol: string;
  }>({ voInCol: 'J', voOutCol: 'K' });

  // Add state for song titles and artists
  const [songTitles, setSongTitles] = useState<{ [key: string]: string }>({});
  const [songArtists, setSongArtists] = useState<{ [key: string]: string }>({});

  // Add new ref for the analyser node
  // const analyserNodeRef = useRef<AnalyserNode | null>(null); // Replaced by state
  const [activeAnalyser, setActiveAnalyser] = useState<AnalyserNode | null>(null);

  // Add new state for individual line audio files
  const [lineAudioFiles, setLineAudioFiles] = useState<{ [key: string]: string }>({});
  const [isUploadingLine, setIsUploadingLine] = useState<string | null>(null);

  const { enqueueSnackbar } = useSnackbar();

  // Add state for per-line gain settings
  const [lineGainSettings, setLineGainSettings] = useState<{ [key: string]: number }>({});

  // Effect to SAVE lineAudioFiles to localStorage when they change
  useEffect(() => {
    if (activeSpreadsheetId && Object.keys(lineAudioFiles).length > 0) {
      try {
        localStorage.setItem(`lineAudioFiles_${activeSpreadsheetId}`, JSON.stringify(lineAudioFiles));
        console.log(`[VOManager] Saved lineAudioFiles to localStorage for ${activeSpreadsheetId}:`, lineAudioFiles);
      } catch (error) {
          console.error(`[VOManager] Error saving lineAudioFiles to localStorage for ${activeSpreadsheetId}:`, error);
          enqueueSnackbar('Error saving line audio data locally. Storage might be full.', { variant: 'warning' });
      }
    } else if (activeSpreadsheetId && Object.keys(lineAudioFiles).length === 0) {
      // If lineAudioFiles becomes empty for an active spreadsheet, clear it from localStorage too
      // This prevents loading stale empty objects if all lines are cleared.
      const wasPreviouslySet = localStorage.getItem(`lineAudioFiles_${activeSpreadsheetId}`);
      if (wasPreviouslySet) { // Only remove if it was actually there
        localStorage.removeItem(`lineAudioFiles_${activeSpreadsheetId}`);
        console.log(`[VOManager] Cleared lineAudioFiles from localStorage for ${activeSpreadsheetId} as it became empty.`);
      }
    }
  }, [lineAudioFiles, activeSpreadsheetId, enqueueSnackbar]);

  // Add effect to save voice settings to localStorage when they change
  useEffect(() => {
    localStorage.setItem('voiceSettings_voiceId', voiceSettings.voiceId);
    localStorage.setItem('voiceSettings_stability', voiceSettings.stability.toString());
    localStorage.setItem('voiceSettings_similarityBoost', voiceSettings.similarityBoost.toString());
  }, [voiceSettings]);

  useEffect(() => {
    if (activeSpreadsheetId) {
      console.log(`[VOManager useEffect] Running cleanup effect for activeSpreadsheetId: ${activeSpreadsheetId}, limiterSettings.gain: ${limiterSettings.gain}`);
      const storageKey = `audioExportSettings_${activeSpreadsheetId}`;
      const existingSettingsString = localStorage.getItem(storageKey);
      console.log(`[VOManager useEffect] Existing localStorage value:`, existingSettingsString);
      let gainToSave = String(limiterSettings.gain); // Use current global gain instead of defaulting to '0'
      console.log(`[VOManager useEffect] Initial gainToSave (from limiterSettings.gain): ${gainToSave}`);

      if (existingSettingsString) {
        try {
          const existingSettings = JSON.parse(existingSettingsString);
          console.log(`[VOManager useEffect] Parsed existing settings:`, existingSettings);
          if (existingSettings.makeupGain !== undefined) {
            gainToSave = String(existingSettings.makeupGain);
            console.log(`[VOManager useEffect] Using existing makeupGain: ${gainToSave}`);
          } else if (existingSettings.gain !== undefined) {
            gainToSave = String(existingSettings.gain);
            console.log(`[VOManager useEffect] Using existing gain: ${gainToSave}`);
          }
        } catch (error) {
          console.error(`[VOManager] Error parsing existing audioExportSettings for ${activeSpreadsheetId}:`, error);
        }
      } else {
        console.log(`[VOManager useEffect] No existing settings found, using limiterSettings.gain: ${gainToSave}`);
      }
      
      const newSettings = { makeupGain: gainToSave };
      localStorage.setItem(storageKey, JSON.stringify(newSettings));
      console.log(`[VOManager useEffect] Final settings saved to localStorage:`, newSettings);
    }
  }, [activeSpreadsheetId, limiterSettings.gain]);

  useEffect(() => {
    localStorage.setItem('voManagerMode', isGenerateMode ? 'generate' : 'upload');
  }, [isGenerateMode]);

  useEffect(() => {
    if (propSpreadsheetId) {
      console.log('VOManager: Using spreadsheetId from props:', propSpreadsheetId);
      setActiveSpreadsheetId(propSpreadsheetId);
      localStorage.setItem('currentSpreadsheetId', propSpreadsheetId);
    } else {
      const storedId = localStorage.getItem('currentSpreadsheetId');
      if (storedId) {
        console.log('VOManager: Found spreadsheetId in localStorage:', storedId);
        setActiveSpreadsheetId(storedId);
      } else {
        console.warn('VOManager: No spreadsheetId found in localStorage. Select a date on the Charts page.');
        setError('Please select a chart date on the main page first.');
      }
    }
  }, [propSpreadsheetId]);

  // Initialize AudioContext only once
  useEffect(() => {
    if (!audioContextRef.current) {
      try {
        audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
        // Create the audio element if it doesn't exist
        if (!audioElementRef.current) {
          audioElementRef.current = new Audio();
        }
      } catch (e) {
        console.error("Web Audio API is not supported in this browser", e);
        setError("Audio processing is not supported in this browser.");
      }
    }

    return () => {
      // Clean up all audio resources when component unmounts
      if (audioElementRef.current) {
        // --- Add Listener Removal --- START ---
        // Remove listeners added dynamically in playAudio to prevent firing during cleanup
        // Create dummy functions to satisfy removeEventListener signature if needed
        const dummyErrorHandler = () => {}; 
        const dummyLoadedMetadataHandler = () => {};
        const dummyEndedHandler = () => {};

        try {
          // Use the same event names as added in playAudio
          audioElementRef.current.removeEventListener('error', dummyErrorHandler); 
          audioElementRef.current.removeEventListener('loadedmetadata', dummyLoadedMetadataHandler);
          audioElementRef.current.removeEventListener('ended', dummyEndedHandler);
          console.log('[VOManager Cleanup] Removed audio element listeners.');
        } catch (removeErr) {
          // Log potential errors during removal but don't block cleanup
          console.warn('[VOManager Cleanup] Error removing audio element listeners:', removeErr); 
        }
        // --- Add Listener Removal --- END ---

        audioElementRef.current.pause();
        audioElementRef.current.src = ''; // Clear src *after* removing listeners
      }
      
      if (sourceNodeRef.current) {
        try {
        sourceNodeRef.current.disconnect();
        } catch (e) { console.warn('[VOManager Cleanup] Error disconnecting source node:', e); }
        sourceNodeRef.current = null;
      }
      
      if (compressorNodeRef.current) {
        try {
        compressorNodeRef.current.disconnect();
        } catch (e) { console.warn('[VOManager Cleanup] Error disconnecting compressor node:', e); }
        compressorNodeRef.current = null;
      }
      
      if (gainNodeRef.current) {
        try {
          gainNodeRef.current.disconnect();
        } catch (e) { console.warn('[VOManager Cleanup] Error disconnecting gain node:', e); }
        // Don't nullify here
      }
      
      // Call setActiveAnalyser(null) on unmount
      setActiveAnalyser(null);

      if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
        console.log('[VOManager Cleanup] Closing AudioContext');
        audioContextRef.current.close().catch(err => {
          console.error('[VOManager Cleanup] Error closing AudioContext:', err);
        });
        audioContextRef.current = null; // Set ref to null after closing
      }
    };
  }, []); // Changed from [activeAnalyser] to [] for one-time setup & unmount cleanup

  // New useEffect to handle activeAnalyser cleanup
  useEffect(() => {
    // This effect runs when activeAnalyser changes or on unmount.
    // The returned cleanup function will clean up the *previous* analyser.
    let analyserToClean = activeAnalyser; // Capture the current activeAnalyser

    return () => {
      if (analyserToClean) {
        console.log('[VOManager Cleanup] Disconnecting an analyser node due to change or unmount.');
        try {
          analyserToClean.disconnect();
        } catch (e) {
          console.warn('[VOManager Cleanup] Error disconnecting analyser node:', e);
        }
        // We might not want to call setActiveAnalyser(null) here, 
        // as a new one might have just been set. 
        // The component that sets activeAnalyser should handle setting it to null if appropriate.
        // However, if the component is unmounting, activeAnalyser should be nullified by the main effect's setActiveAnalyser(null) call,
        // but that was removed to put it here. Let's reconsider.
        // The main useEffect's cleanup calls setActiveAnalyser(null) - no, that was part of the block I removed.
        // The issue is, when a new analyser is set, this cleanup runs for the *old* one.
        // When the component unmounts, this cleanup also runs.

        // Let's rethink. The original block in the main useEffect was:
        // if (activeAnalyser) {
        //   try { activeAnalyser.disconnect(); } catch (e) { /* ... */ }
        //   setActiveAnalyser(null); 
        // }
        // This `setActiveAnalyser(null)` is important on unmount.

        // Perhaps the new useEffect should only handle disconnect.
        // And the main unmount effect should ensure setActiveAnalyser(null) is called.
        // Let's add setActiveAnalyser(null) back to the main unmount effect's activeAnalyser block if we reinstate it,
        // or ensure it's called on unmount some other way.

        // For now, let's just disconnect. The playAudio function sets the new analyser,
        // and stopAudio sets it to null.
      }
    };
  }, [activeAnalyser]); // This effect depends on activeAnalyser

  // Apply global gain settings when they change
  useEffect(() => {
    const audioContext = audioContextRef.current;
    if (!audioContext) return;
    
    try {
      // Clean up previous gain node if it exists
      if (gainNodeRef.current) {
        gainNodeRef.current.disconnect();
      }
      // The compressorNodeRef is no longer used, ensure it's disconnected if it was somehow set
      if (compressorNodeRef.current) {
        compressorNodeRef.current.disconnect();
        compressorNodeRef.current = null; // Clear the ref
      }
      
      // Create global gain node
      gainNodeRef.current = audioContext.createGain();
      const makeupGain = limiterSettings.gain;
      const linearGain = Math.pow(10, makeupGain / 20); // Convert dB to linear gain
      gainNodeRef.current.gain.value = linearGain;
      
      console.log(`Applied global gain setting: makeupGain=${makeupGain}dB (linear gain: ${linearGain.toFixed(4)})`);
      
      // Also ensure the export settings are synced whenever gain changes
      if (activeSpreadsheetId) {
        const storageKey = `audioExportSettings_${activeSpreadsheetId}`;
        const settingsForExport = { makeupGain: String(makeupGain) };
        localStorage.setItem(storageKey, JSON.stringify(settingsForExport));
        console.log(`[VOManager gainSync] Auto-synced export settings for gain change: ${makeupGain}dB to ${storageKey}`);
      }
    } catch (err) {
      console.error('Error setting up global gain node:', err);
    }
  }, [limiterSettings, activeSpreadsheetId]);

  // Add effect to ensure export settings always use global gain (not line-specific gains)
  useEffect(() => {
    if (activeSpreadsheetId && limiterSettings.gain !== undefined) {
      // Force restore global gain to export settings whenever activeSpreadsheetId or global gain changes
      const storageKey = `audioExportSettings_${activeSpreadsheetId}`;
      const settingsForExport = { makeupGain: String(limiterSettings.gain) };
      localStorage.setItem(storageKey, JSON.stringify(settingsForExport));
      console.log(`[VOManager autoRestore] Ensured export settings use global gain: ${limiterSettings.gain}dB (${storageKey})`);
    }
  }, [activeSpreadsheetId, limiterSettings.gain]);

  const fetchVOData = useCallback(async (): Promise<void> => {
    try {
      setLoading(true);
      setError('');

    if (!activeSpreadsheetId) {
        setError('No spreadsheet ID provided');
        setLoading(false);
      return;
    }

      console.log('VOManager: Found spreadsheetId in localStorage:', activeSpreadsheetId);

      // First, get the header row to identify columns
      const headerData = await sheetsService.getData(activeSpreadsheetId, 'Sheet1!1:1');
      
      // Find positions of important columns
      let voTextColIndex = 11; // Default to column L for VO Text
      let voInColIndex = 9;    // Default to column J for VO In point
      let voOutColIndex = 10;  // Default to column K for VO Out point
      // Add defaults for song title and artist columns
      let songTitleColIndex = 2; // Default to column C
      let songArtistColIndex = 3; // Default to column D
      
      if (headerData.values && headerData.values[0]) {
        const headerRow = headerData.values[0];
        
        // Try to find column positions based on header names
        const voTextColPos = headerRow.findIndex((cell: string) => 
          cell?.toString().toLowerCase().includes('vo text'));
        
        const voInColPos = headerRow.findIndex((cell: string) => 
          cell?.toString().toLowerCase().includes('vo in') || 
          cell?.toString().toLowerCase() === 'in');
        
        const voOutColPos = headerRow.findIndex((cell: string) => 
          cell?.toString().toLowerCase().includes('vo out') || 
          cell?.toString().toLowerCase() === 'out');
        
        // Add detection for song title and artist columns
        // Try exact matches first ("Song", "Artist")
        let songTitleColPos = headerRow.findIndex((cell: string) => 
          cell?.toString().toLowerCase() === 'song'); // Exact match for "Song"
        let songArtistColPos = headerRow.findIndex((cell: string) => 
          cell?.toString().toLowerCase() === 'artist'); // Exact match for "Artist"

        // Fallback to includes if specific matches fail
        if (songTitleColPos === -1) {
            songTitleColPos = headerRow.findIndex((cell: string) => 
              cell?.toString().toLowerCase().includes('title')); // Fallback: includes "title"
        }
        // Corrected self-assignment for songArtistColPos
        if (songArtistColPos === -1) {
            const newArtistColPos = headerRow.findIndex((cell: string) => 
              cell?.toString().toLowerCase().includes('artist'));
            if (newArtistColPos !== -1) {
                 songArtistColPos = newArtistColPos;
            } // If not found, songArtistColPos remains -1, no self-assign
        }
        
        // Use found positions if available
        if (voTextColPos !== -1) voTextColIndex = voTextColPos;
        if (voInColPos !== -1) voInColIndex = voInColPos;
        if (voOutColPos !== -1) voOutColIndex = voOutColPos;
        // Update indices if found
        if (songTitleColPos !== -1) songTitleColIndex = songTitleColPos;
        // if (songArtistColPos !== -1) songArtistColPos = songArtistColPos; // Removed self-assignment
        
        // Add a check: If indices end up the same, something is wrong
        if (songTitleColIndex === songArtistColIndex && songTitleColIndex !== 2 && songArtistColIndex !== 3) { // Check if they ended up the same valid index, ignore if both are default
            console.warn(`Warning: Detected Title and Artist columns are the same (${songTitleColIndex}). Check sheet headers for ambiguity.`);
            // Optional: Revert artist index to default if they clash?
            // songArtistColIndex = 3; // Revert to default D (index 3)
        }
        
        console.log('Using column positions - Text:', voTextColIndex, 
                   'In:', voInColIndex, 'Out:', voOutColIndex);
        console.log('Using column positions - Title:', songTitleColIndex, 
                   'Artist:', songArtistColIndex);
      }
      
      // Convert column indices to letters (e.g., 0 = A, 1 = B, etc.)
      const voTextCol = String.fromCharCode(65 + voTextColIndex);
      const voInCol = String.fromCharCode(65 + voInColIndex);
      const voOutCol = String.fromCharCode(65 + voOutColIndex);
      // Convert title and artist indices to letters
      const songTitleCol = String.fromCharCode(65 + songTitleColIndex);
      const songArtistCol = String.fromCharCode(65 + songArtistColIndex);
      
      // Store the detected columns in state for use in timestamp saving
      setDetectedColumns({ voInCol, voOutCol });
      
      // Also set columnMappings to mirror these values for saving timestamps
      setColumnMappings({
        voInColLetter: voInCol,
        voOutColLetter: voOutCol
      });
      
      // Prepare ranges for batch request - get rows 2-13 for all three columns
      const ranges = [];
      for (let row = 2; row <= 13; row++) {
        ranges.push(`Sheet1!${voTextCol}${row}`);
        ranges.push(`Sheet1!${voInCol}${row}`);
        ranges.push(`Sheet1!${voOutCol}${row}`);
        // Add ranges for song title and artist
        ranges.push(`Sheet1!${songTitleCol}${row}`);
        ranges.push(`Sheet1!${songArtistCol}${row}`);
      }
      
      // Make a single batch request instead of many individual requests
      const batchData = await sheetsService.batchGetValues(activeSpreadsheetId, ranges);
      
      // Process the batch results to extract VO text and In/Out points
      const voData: { [key: string]: string } = {};
      const voInPoints: { [key: string]: string } = {};
      const voOutPoints: { [key: string]: string } = {};
      // Initialize maps for title and artist
      const songTitlesMap: { [key: string]: string } = {};
      const songArtistsMap: { [key: string]: string } = {};
      
      // Map rows to keys in DISPLAY_ORDER
      const rowToKeyMap: { [key: number]: string } = {
        2: '10', 3: '9', 4: '8', 5: '7', 6: '6', 7: '5', 
        8: '4', 9: '3', 10: '2', 11: '1', 12: 'Intro', 13: 'Outro'
      };
      
      for (let row = 2; row <= 13; row++) {
        const key = rowToKeyMap[row] || `row-${row}`;
        const textRange = `Sheet1!${voTextCol}${row}`;
        const inRange = `Sheet1!${voInCol}${row}`;
        const outRange = `Sheet1!${voOutCol}${row}`;
        // Define ranges for title and artist
        const titleRange = `Sheet1!${songTitleCol}${row}`;
        const artistRange = `Sheet1!${songArtistCol}${row}`;
        
        // Extract values from the response
        const textData = batchData[textRange];
        const inData = batchData[inRange];
        const outData = batchData[outRange];
        // Extract title and artist data
        const titleData = batchData[titleRange];
        const artistData = batchData[artistRange];
        
        const voText = textData?.values?.[0]?.[0] || '';
        const inPoint = inData?.values?.[0]?.[0] || '';
        const outPoint = outData?.values?.[0]?.[0] || '';
        // Extract title and artist values
        const songTitle = titleData?.values?.[0]?.[0] || '';
        const songArtist = artistData?.values?.[0]?.[0] || '';
        
        if (voText) {
          voData[key] = voText;
          // Also store the in/out points
          voInPoints[key] = inPoint;
          voOutPoints[key] = outPoint;
          // Store title and artist
          songTitlesMap[key] = songTitle;
          songArtistsMap[key] = songArtist;
        }
      }
      
      setVoData(voData);
      setVoInPoints(voInPoints);
      setVoOutPoints(voOutPoints);
      // Set state for titles and artists
      setSongTitles(songTitlesMap);
      setSongArtists(songArtistsMap);
      
      setLoading(false);
    } catch (err) {
      console.error('Error fetching VO data:', err);
      setError(`Google Sheets error: ${err instanceof Error ? err.message : String(err)}`);
      setLoading(false);
    }
  }, [activeSpreadsheetId]);

  useEffect(() => {
    if (activeSpreadsheetId) {
        fetchVOData();
        // Attempt to load the persisted audio URL and peaks for this spreadsheet
        const storedDataString = localStorage.getItem(`processedAudioData_${activeSpreadsheetId}`);
        if (storedDataString) {
            try {
              const storedData = JSON.parse(storedDataString);
              if (storedData.url) {
                // Ensure URL has the API base if it's a relative path
                const fullUrl = storedData.url.startsWith('http') 
                  ? storedData.url 
                  : `${API_BASE_URL}${storedData.url}`;
                setProcessedAudioUrl(fullUrl);
                console.log(`[VOManager] Restored audio URL from localStorage: ${fullUrl}`);
              }
              
              if (storedData.peaks && Array.isArray(storedData.peaks)) {
                setProcessedAudioPeaks(storedData.peaks);
                console.log(`[VOManager] Restored ${storedData.peaks.length} peak values from localStorage`);
              }
            } catch (e) {
              console.error('Error parsing stored audio data:', e);
            }
        }
        
        // Load line-specific audio files
        const lineAudioFilesKey = `lineAudioFiles_${activeSpreadsheetId}`;
        const storedLineAudioFiles = localStorage.getItem(lineAudioFilesKey);
        if (storedLineAudioFiles) {
          try {
            const parsedLineAudioFiles = JSON.parse(storedLineAudioFiles);
            setLineAudioFiles(parsedLineAudioFiles);
          } catch (e) {
            console.error('Error parsing stored line audio files:', e);
          }
        }
        
        // Load in/out points from localStorage
        const inPointsKey = `voInPoints_${activeSpreadsheetId}`;
        const outPointsKey = `voOutPoints_${activeSpreadsheetId}`;
        const storedInPoints = localStorage.getItem(inPointsKey);
        const storedOutPoints = localStorage.getItem(outPointsKey);
        
        // Load line gain settings from localStorage
        const lineGainKey = `lineGainSettings_${activeSpreadsheetId}`;
        const storedLineGain = localStorage.getItem(lineGainKey);
        
        try {
          if (storedInPoints) {
            setVoInPoints(JSON.parse(storedInPoints));
          }
          if (storedOutPoints) {
            setVoOutPoints(JSON.parse(storedOutPoints));
          }
          if (storedLineGain) {
            const parsedLineGain = JSON.parse(storedLineGain);
            console.log('[VOManager Load Effect] Parsed lineGainSettings from localStorage:', parsedLineGain); // Added Log
            setLineGainSettings(parsedLineGain);
          }
        } catch (e) {
          console.error('Error parsing stored timestamps or gain settings:', e);
        }
    }
  }, [activeSpreadsheetId, fetchVOData]); // Added fetchVOData dependency

  const handleCellClick = (key: string) => {
    if (isGeneratingAll || !activeSpreadsheetId) return;
    // If this is the Outro field and it's empty, populate with the default text
    if (key === 'Outro' && (!voData[key] || voData[key].trim() === '')) {
      setEditingCell({ key, value: DEFAULT_OUTRO_TEXT });
    } else {
      setEditingCell({ key, value: voData[key] || '' });
    }
  };

  const handleCellEditChange = (key: string, value: string) => {
    // Remove the special handling for Outro here
      setEditingCell({ key, value });
  };

  const handleSave = async (key: string, value: string) => {
    if (!key || !activeSpreadsheetId) {
      setError('Cannot save: No spreadsheet selected.')
      return;
    }

    const cellAddress = CELL_MAP[key];
    if (!cellAddress) {
      setError(`Invalid key: ${key}`);
      setEditingCell(null);
      return;
    }

    const range = `Sheet1!${cellAddress}`;
    console.log(`Saving "${value}" to ${activeSpreadsheetId}, range: ${range}`);

    const previousValue = voData[key];
    setVoData((prev) => ({ ...prev, [key]: value }));
    setEditingCell(null);

    try {
      // Use batch update method for consistency, even though it's just one update
      console.log('[VOManager][handleSave] About to write to sheet', {
        spreadsheetId: activeSpreadsheetId,
        range,
        values: [[value]]
      });
      await sheetsService.batchUpdateValues(activeSpreadsheetId, [
        { range, values: [[value]] }
      ]);
      setError(null);
      await sheetsService.batchUpdateValues(activeSpreadsheetId, [
        { range, values: [[value]] }
      ]);
      setError(null);
    } catch (err) {
      console.error('Error saving data:', err);
      setError(err instanceof Error ? err.message : 'Failed to save data');
      setVoData((prev) => ({ ...prev, [key]: previousValue }));
    }
  };

  const playAudio = useCallback((key: string) => {
    try {
      // For generate mode, we use generated audio
      // For upload mode, we play a segment from the processed audio
      let audioSource = '';
      
      if (isGenerateMode) {
        audioSource = generatedAudio[key] || '';
      } else if (key === 'Outro' && (
        // Use default outro audio if script field is empty OR both in/out points are 0
        (!voData[key] || voData[key].trim() === '') || 
        (voInPoints[key] === '0' && voOutPoints[key] === '0')
      )) {
        // Play default Outro audio when script is empty OR in/out points are both 0
        console.log('[VOManager] Playing default Outro audio because script field is empty or in/out points are both 0.');
        audioSource = DEFAULT_OUTRO_AUDIO_URL;
      } else {
        // For other keys in upload mode, OR for Outro with a non-empty script,
        // check line-specific or main URL
        if (lineAudioFiles[key]) {
          audioSource = lineAudioFiles[key];
          console.log(`Using line-specific audio: ${lineAudioFiles[key]}`);
        } else if (processedAudioUrl) {
          audioSource = processedAudioUrl;
        } else {
          throw new Error('No audio source available');
        }
      }

      if (!audioSource) {
        throw new Error(`No audio source available for ${key}`);
      }
    
      console.log(`Playing audio for ${key}`);

      // Always create a new audio element to avoid reconnection issues
      const audioElement = new Audio();
    
      // Add error handling before setting source to catch CORS and other load errors
      audioElement.addEventListener('error', (event) => {
        console.error('Audio playback error:', event);
        setPlayingKey(null);
    
        // Show error toast
        if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
          enqueueSnackbar(`Audio playback error: Unable to load audio file. Please check server configuration.`, { 
            variant: 'error',
            autoHideDuration: 5000
          });
        } else {
          console.log('[VOManager] Suppressing error notification - component likely unmounted');
        }
        
        // Clean up any existing audio processing
      if (sourceNodeRef.current) {
        try {
          sourceNodeRef.current.disconnect();
        sourceNodeRef.current = null;
        } catch (err) {
            console.error('Error disconnecting source node:', err);
        }
        }
      }, { once: true });

      // Set audio properties and source
      audioElement.crossOrigin = "anonymous";  // Critical for CORS audio processing
      audioElement.src = audioSource;
      audioElementRef.current = audioElement;

      // Create or get audio context
      let audioContext = audioContextRef.current;
      if (!audioContext || audioContext.state === 'closed') {
        try {
          audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
          audioContextRef.current = audioContext;
        } catch (error) {
          console.error('Failed to create AudioContext:', error);
          throw new Error('Could not create audio context. Please check your browser settings.');
      }
      } else if (audioContext.state === 'suspended') {
        audioContext.resume().catch((error) => {
          console.error('Failed to resume AudioContext:', error);
          throw new Error('Could not resume audio context. Please check your browser settings.');
        });
      }
      
      setPlayingKey(key);
      
      // Wait for audio element to load metadata before connecting to avoid InvalidStateError
      audioElement.addEventListener('loadedmetadata', () => {
        try {
          // Make sure audioContext is not null before proceeding
          if (!audioContext) {
            throw new Error('AudioContext is not available');
          }

          // Create a new source node from the audio element
          const sourceNode = audioContext.createMediaElementSource(audioElement);
          sourceNodeRef.current = sourceNode; // Keep track of the source

          // Create a new AnalyserNode for this playback instance
          const newAnalyser = audioContext.createAnalyser();
          newAnalyser.fftSize = 256; // Or your preferred fftSize
          
          // --- START Line-specific Gain Adjustment ---
          const lineGainDb = lineGainSettings[key] || 0;
          const linearLineGain = Math.pow(10, lineGainDb / 20);
          const lineSpecificGainNode = audioContext.createGain();
          lineSpecificGainNode.gain.value = linearLineGain;
          console.log(`[VOManager] Applying line-specific gain for ${key}: ${lineGainDb}dB (linear: ${linearLineGain.toFixed(4)})`);
          
          // Connect source to line-specific gain
          sourceNode.connect(lineSpecificGainNode);
          let currentNode: AudioNode = lineSpecificGainNode;
          console.log('[VOManager] Audio routing: source -> lineGain');

          // Always connect to the global gain node (gainNodeRef.current) after line-specific gain
          if (gainNodeRef.current) {
            currentNode.connect(gainNodeRef.current);
            currentNode = gainNodeRef.current;
            console.log('[VOManager] Audio routing: ... -> globalGain');
          } else {
            console.warn('[VOManager] Global gain node (gainNodeRef.current) is not available. Skipping global gain stage.');
          }
          
          // Now, connect from global gain (or line gain if global is missing) to analyser
          // Compressor stage is removed.
          console.log('[VOManager] Audio routing: ... -> analyser -> destination (compressor removed)');
          currentNode.connect(newAnalyser);
          newAnalyser.connect(audioContext.destination);

          // Update the ref *before* setting playingKey to ensure VUMeter gets the new analyser
          setActiveAnalyser(newAnalyser);
          
          // For upload mode with main audio file, we need to set specific time segments
          if (!isGenerateMode && (audioSource === processedAudioUrl || lineAudioFiles[key]) && key !== 'Outro') {
            const inPoint = voInPoints[key];
            const outPoint = voOutPoints[key];
            
            if (inPoint && outPoint) {
              const inSeconds = parseFloat(inPoint);
              const outSeconds = parseFloat(outPoint);
              
              console.log(`Setting time range: ${inSeconds}s to ${outSeconds}s`);
              
              audioElement.currentTime = inSeconds;
      
              // Set up ended event based on out point
              const durationSeconds = outSeconds - inSeconds;
              if (durationSeconds > 0) {
                setTimeout(() => {
          audioElement.pause();
          setPlayingKey(null);
                }, durationSeconds * 1000);
              }
            }
      }
      
      // Play the audio
      audioElement.play()
            .catch(error => {
              console.error('Error during audio playback:', error);
              enqueueSnackbar(`Playback error: ${error.message}`, { 
                variant: 'error',
                autoHideDuration: 3000
              });
          setPlayingKey(null);
        });
            
        } catch (error) {
          console.error('Error setting up audio nodes:', error);
          enqueueSnackbar(`Audio setup error: ${error instanceof Error ? error.message : 'Unknown error'}`, { 
            variant: 'error',
            autoHideDuration: 3000
          });
          setPlayingKey(null);
        }
      }, { once: true });
      
      // Handle audio ending
      audioElement.addEventListener('ended', () => {
        setPlayingKey(null);
      }, { once: true });
      
    } catch (error) {
      console.error('Playback error:', error);
      enqueueSnackbar(`Playback error: ${error instanceof Error ? error.message : 'Unknown error'}`, { 
        variant: 'error',
        autoHideDuration: 3000
      });
    }
  }, [
    isGenerateMode, 
    generatedAudio, 
    processedAudioUrl, 
    lineAudioFiles, 
    voInPoints, 
    voOutPoints, 
    enqueueSnackbar, 
    voData, 
    setActiveAnalyser, 
    sourceNodeRef, 
    gainNodeRef,
    lineGainSettings
  ]);

  const stopAudio = useCallback(() => {
    // Stop any currently playing audio
    if (audioElementRef.current) {
      audioElementRef.current.pause();
    }
    // It's also good practice to disconnect the analyser when stopping
    if (activeAnalyser) { // Check activeAnalyser state
      activeAnalyser.disconnect();
      setActiveAnalyser(null); // Clear it
    }
    setPlayingKey(null);
  }, [setPlayingKey, activeAnalyser, setActiveAnalyser]); // Added activeAnalyser and setActiveAnalyser based on new linter warning (and prior removal reasoning)

  const handleVOButtonClick = (key: string) => {
    if (isGeneratingAll) return;
    
    try {
      if (playingKey === key) {
        stopAudio();
      } else {
        if (playingKey) {
          stopAudio();
        }
        
        if (isGenerateMode) {
          const audioData = generatedAudio[key];
          if (audioData) {
            playAudio(key);
          } else {
            setError(`No generated audio found for "${key}"`);
          }
        } else {
          // Special handling for Outro with empty script or 0/0 points
          if ((key === 'Outro' && ((!voData[key] || voData[key].trim() === '')||(voInPoints[key] === '0' && voOutPoints[key] === '0'))) || processedAudioUrl) {
            // Check if audio context needs to be resumed
            if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
              audioContextRef.current.resume()
                .then(() => playAudio(key))
                .catch(err => {
                  console.error('Error resuming AudioContext:', err);
                  setError(`Error resuming audio: ${err instanceof Error ? err.message : String(err)}`);
                });
            } else {
              playAudio(key);
            }
          } else {
            setError('No processed audio file available');
          }
        }
      }
    } catch (err) {
      console.error('Error in VO button click handler:', err);
      setError(`Playback error: ${err instanceof Error ? err.message : String(err)}`);
      // Reset state in case of error
      setPlayingKey(null);
    }
  };

  const handleGenerateAll = async () => {
    if (!activeSpreadsheetId || isGeneratingAll) return;
    setIsGeneratingAll(true);
    setError(null);

    try {
      // Generate audio for all entries that don't have it
      for (const key of DISPLAY_ORDER) {
        if (!generatedAudio[key] && voData[key]) {
          await generateAudio(key, voData[key]);
        }
      }
    } catch (err) {
      console.error('Error generating all audio:', err);
      setError(`Error generating all audio: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setIsGeneratingAll(false);
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsProcessing(true);
    setError(null);
    setUploadProgress(0);

    try {
      const formData = new FormData();
      formData.append('audio', file);
      
      // Add script data to the request
      formData.append('scriptData', JSON.stringify(voData));

      console.log(`[VOManager] Uploading file: ${file.name}`);

      const response = await fetch(`${API_BASE_URL}/api/process-stt-gemini`, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json() as TranscriptionResult;
      if (result.fileUrl) {
        // Ensure the URL has the full base URL
        const fullUrl = result.fileUrl.startsWith('http') 
          ? result.fileUrl 
          : `${API_BASE_URL}${result.fileUrl}`;
        
        console.log(`[VOManager] Setting processedAudioUrl to: ${fullUrl}`);
        setProcessedAudioUrl(fullUrl);
        
        // Save the URL to localStorage - no longer saving to Google Sheet
        if (activeSpreadsheetId) {
          const storageData = {
            url: fullUrl,
            peaks: result.peaks || []
          };
          localStorage.setItem(`processedAudioData_${activeSpreadsheetId}`, JSON.stringify(storageData));
          console.log(`[VOManager] Saved audio URL to localStorage: ${fullUrl}`);
        }
      }
      
      // Update the transcription result with the new data
      setTranscriptionResult(result);
      
      // Update the in/out points based on the timestamps
      if (result.timestamps) {
        const newInPoints: { [key: string]: string } = {};
        const newOutPoints: { [key: string]: string } = {};
        
        Object.entries(result.timestamps).forEach(([key, value]) => {
          if (value.inPoint !== undefined) {
            newInPoints[key] = value.inPoint.toString();
          }
          if (value.outPoint !== undefined) {
            newOutPoints[key] = value.outPoint.toString();
          }
        });
        
        setVoInPoints(newInPoints);
        setVoOutPoints(newOutPoints);
        
        // Save the in/out points to Google Sheets
        if (activeSpreadsheetId) {
          try {
            console.log('[VOManager] Saving timestamps to Google Sheet');
            const updates: { range: string, values: any[][] }[] = [];
            
            // Use the detected column letters
            const voInCol = detectedColumns.voInCol;
            const voOutCol = detectedColumns.voOutCol;
            
            // Prepare batch updates for each key with timestamps
            Object.entries(newInPoints).forEach(([key, inPoint]) => {
              // Map the key to the correct row number
              const rowMap: { [key: string]: number } = {
                'Intro': 12, '10': 2, '9': 3, '8': 4, '7': 5, '6': 6, 
                '5': 7, '4': 8, '3': 9, '2': 10, '1': 11, 'Outro': 13
              };
              
              const row = rowMap[key];
              if (row) {
                // Add in point to the updates array
                updates.push({
                  range: `Sheet1!${voInCol}${row}`,
                  values: [[inPoint]]
                });
                
                // Add out point to the updates array if it exists
                const outPoint = newOutPoints[key];
                if (outPoint) {
                  updates.push({
                    range: `Sheet1!${voOutCol}${row}`,
                    values: [[outPoint]]
                  });
                }
              }
            });
            
            // Execute batch update if there are updates to make
            if (updates.length > 0) {
              await sheetsService.batchUpdateValues(activeSpreadsheetId, updates);
              console.log(`[VOManager] Successfully saved ${updates.length} timestamp values to Google Sheet`);
            }
          } catch (sheetError) {
            console.error('Error saving timestamps to Google Sheet:', sheetError);
            // Continue even if saving to sheet fails - we'll still have the values in state
          }
        }
      }
      
      setIsUploadSuccess(true);
      // Note: URL is already saved to localStorage earlier in this function
    } catch (err) {
      console.error('Error uploading file:', err);
      setError(`Error uploading file: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleInPointClick = (key: string) => {
    if (isGeneratingAll) return;
    setEditingCell({ key: `in_${key}`, value: voInPoints[key] || '' });
  };

  const handleOutPointClick = (key: string) => {
    if (isGeneratingAll) return;
    setEditingCell({ key: `out_${key}`, value: voOutPoints[key] || '' });
  };

  const handleInPointSave = (key: string, value: string) => {
    setVoInPoints(prev => ({ ...prev, [key]: value }));
    setEditingCell(null);
  };

  const handleOutPointSave = (key: string, value: string) => {
    setVoOutPoints(prev => ({ ...prev, [key]: value }));
    setEditingCell(null);
  };

  const handleSaveSettings = (newSettings: typeof voiceSettings) => {
    setVoiceSettings(newSettings);
    setIsSettingsOpen(false);
  };

  const generateAudio = async (key: string, text: string) => {
    if (!text || !activeSpreadsheetId) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/generate-audio`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          text,
          voiceId: voiceSettings.voiceId,
          stability: voiceSettings.stability,
          similarityBoost: voiceSettings.similarityBoost,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      setGeneratedAudio(prev => ({ ...prev, [key]: result.audioUrl }));
      return result.audioUrl;
    } catch (err) {
      console.error(`Error generating audio for ${key}:`, err);
      setError(`Error generating audio for ${key}: ${err instanceof Error ? err.message : String(err)}`);
      throw err;
    }
  };

  const handleScriptSave = async (parsedScript: any) => {
    setScriptDialogOpen(false);
    
    console.log('[handleScriptSave] Starting script save with data:', parsedScript);
    
    // Update the VO data with the new script
    const newVoData = { ...voData };
    
    // Handle intro if present
    if (typeof parsedScript.intro === 'string') {
      newVoData['Intro'] = parsedScript.intro;
      console.log(`[handleScriptSave] Setting Intro: "${parsedScript.intro}"`);
    }
    
    // Handle the songs array
    if (Array.isArray(parsedScript.songs)) {
      console.log(`[handleScriptSave] Processing ${parsedScript.songs.length} songs`);
      
      // Map songs array to positions (10 down to 1)
      parsedScript.songs.forEach((songText: string, index: number) => {
        // Calculate position in reverse order - index 0 should be 10, index 1 should be 9, etc.
        const position = (10 - index).toString();
        console.log(`[handleScriptSave] Mapping song ${index} to position ${position}: "${songText}"`);
        newVoData[position] = songText;
      });
    } else {
      console.log('[handleScriptSave] No songs array found in parsedScript');
    }
    
    // Handle outro if present
    if (typeof parsedScript.outro === 'string') {
      newVoData['Outro'] = parsedScript.outro;
      console.log(`[handleScriptSave] Setting Outro: "${parsedScript.outro}"`);
      }
    
    // Log the final newVoData object
    console.log('[handleScriptSave] Final newVoData:', Object.keys(newVoData));
    
    setVoData(newVoData);
    
    // Save to Google Sheets
    if (activeSpreadsheetId) {
      try {
        // Filter entries to only include those with valid cell mappings
        const updates = Object.entries(newVoData)
          .filter(([key]) => CELL_MAP[key] !== undefined) 
          .map(([key, value]) => {
            const cellRef = CELL_MAP[key];
            // Ensure we have a valid reference to the cell
            if (!cellRef) {
              console.warn(`[handleScriptSave] No cell reference found for key: ${key}`);
              return null;
            }
            const range = `Sheet1!${cellRef}:${cellRef}`;
            console.log(`[handleScriptSave] Preparing update for ${key} at ${range}`);
            return { range, values: [[value]] };
          })
          .filter((update): update is { range: string; values: string[][] } => update !== null); // Filter out null updates with type guard

        console.log(`[handleScriptSave] Total updates to apply: ${updates.length}`);

        // Apply each update individually
        if (updates.length > 0) {
          console.log(`[handleScriptSave] Applying ${updates.length} updates to sheet...`);
          try {
            for (const update of updates) {
              console.log(`[handleScriptSave] Sending update: ${update.range} = ${JSON.stringify(update.values)}`);
              await sheetsService.updateData(activeSpreadsheetId, update.range, update.values);
              console.log(`[handleScriptSave] Update sent successfully: ${update.range}`);
            }
            console.log('[handleScriptSave] Updates applied successfully.');
          } catch (updateErr) {
            console.error('[handleScriptSave] Error during sheet update:', updateErr);
            throw updateErr; // Re-throw to be caught by outer catch
          }
        } else {
          console.log('[handleScriptSave] No valid updates to apply after filtering.');
        }

      } catch (err) {
        console.error('Error saving script to sheets:', err);
        setError(`Error saving script to sheets: ${err instanceof Error ? err.message : String(err)}`);
        enqueueSnackbar(`Failed to save script to Google Sheet: ${err instanceof Error ? err.message : String(err)}`, { variant: 'error' });
      }
    } else {
      console.error('[handleScriptSave] No active spreadsheet ID found');
      setError('No active spreadsheet ID found. Please select a chart date first.');
      enqueueSnackbar('No active spreadsheet ID found. Please select a chart date first.', { variant: 'error' });
    }
  };

  const handleLimiterChange = (newSettings: {
    gain: number; // This is the makeupGain we want to save
  }) => {
    console.log(`[VOManager handleLimiterChange] Called with gain: ${newSettings.gain}, activeSpreadsheetId: ${activeSpreadsheetId}`);
    setLimiterSettings(newSettings);
    localStorage.setItem('limiterSettings_gain', String(newSettings.gain));
    console.log(`[VOManager handleLimiterChange] Saved limiterSettings_gain to localStorage: ${newSettings.gain}`);

    // --- Save settings for OutputManager export --- START ---
    if (!activeSpreadsheetId) {
      console.error('[VOManager] Cannot save settings to localStorage for OutputManager: activeSpreadsheetId is missing.');
      return;
    }
    const settingsForExport = {
      makeupGain: String(newSettings.gain), // This is the key one - GLOBAL gain, not line-specific
    };
    const storageKey = `audioExportSettings_${activeSpreadsheetId}`;
    try {
      localStorage.setItem(storageKey, JSON.stringify(settingsForExport));
      console.log(`[VOManager handleLimiterChange] Saved audio export settings to localStorage (${storageKey}) from GlobalGainDialog:`, settingsForExport);
      
      // Verify it was saved correctly
      const verification = localStorage.getItem(storageKey);
      console.log(`[VOManager handleLimiterChange] Verification - localStorage now contains:`, verification);
    } catch (error) {
      console.error('[VOManager] Failed to save audio export settings to localStorage:', error);
    }
    // --- Save settings for OutputManager export --- END ---
  };

  // Add a function to restore global gain settings (call this when you want to reset export to global gain)
  const restoreGlobalGainToExport = useCallback(() => {
    if (!activeSpreadsheetId) {
      console.warn('[VOManager] Cannot restore global gain: activeSpreadsheetId is missing.');
      return;
    }
    
    const currentGlobalGain = limiterSettings.gain;
    const settingsForExport = {
      makeupGain: String(currentGlobalGain),
    };
    const storageKey = `audioExportSettings_${activeSpreadsheetId}`;
    
    try {
      localStorage.setItem(storageKey, JSON.stringify(settingsForExport));
      console.log(`[VOManager restoreGlobalGainToExport] Restored global gain ${currentGlobalGain}dB to export settings (${storageKey})`);
    } catch (error) {
      console.error('[VOManager] Failed to restore global gain to export settings:', error);
    }
  }, [activeSpreadsheetId, limiterSettings.gain]);

  const handleOpenWaveformEditor = (key: string) => {
    let audioUrl: string | null = null;
    let inPoint: number | null = null;
    let outPoint: number | null = null;
    let isDefaultOutro = false;

    // Determine the source and points
    if (key === 'Outro' && (
      // Use default outro when script is empty OR both in/out points are 0
      (!voData[key] || voData[key].trim() === '') || 
      (voInPoints[key] === '0' && voOutPoints[key] === '0')
    )) {
      // Special case: Default Outro with empty script or zero in/out points
      audioUrl = DEFAULT_OUTRO_AUDIO_URL;
      inPoint = 0;
      outPoint = 7; // Set specific out point for default outro
      isDefaultOutro = true;
      console.log('[VOManager handleOpenWaveformEditor] Preparing editor for DEFAULT Outro.');
    } else {
      // Standard case (or Outro with script)
      audioUrl = lineAudioFiles[key] || processedAudioUrl;
      if (voInPoints[key] && voOutPoints[key]) {
        inPoint = parseFloat(voInPoints[key]);
        outPoint = parseFloat(voOutPoints[key]);
      } else {
        // If standard case is missing points, we cannot open
        console.warn(`[VOManager handleOpenWaveformEditor] Cannot open editor for key '${key}': Missing in/out points.`);
        return;
      }
    }

    // Check if we have a valid URL and points before proceeding
    if (!audioUrl || inPoint === null || outPoint === null || isNaN(inPoint) || isNaN(outPoint)) {
      console.warn(`[VOManager handleOpenWaveformEditor] Cannot open editor for key '${key}': Invalid URL or points (URL: ${audioUrl}, In: ${inPoint}, Out: ${outPoint}).`);
      return;
    }

    const gainToPass = lineGainSettings[key] || 0;
    console.log(`[VOManager handleOpenWaveformEditor] Preparing editor data for key '${key}' (DefaultOutro: ${isDefaultOutro}) with initialGain: ${gainToPass}dB`);

    // Set the editor data FIRST
    setEditorData({
      lineKey: key,
      initialInPoint: inPoint,
      initialOutPoint: outPoint,
      initialGain: gainToPass,
      audioUrl: audioUrl,
      songTitle: isDefaultOutro ? 'Default Outro' : songTitles[key],
      songArtist: isDefaultOutro ? '' : songArtists[key]
    });
    
    // Set the key we want to open
    setPendingEditorKey(key);
  };

  // Effect to open the editor *after* editorData has been set
  useEffect(() => {
    if (pendingEditorKey && editorData?.lineKey === pendingEditorKey) {
      console.log(`[VOManager Effect] editorData updated for key '${pendingEditorKey}', opening dialog.`);
    setIsEditorOpen(true);
      setPendingEditorKey(null); // Reset pending state
    }
  }, [editorData, pendingEditorKey]); // Depend on editorData and the pending key

  // Add onClose handler to clear editorData when dialog closes
  const handleCloseEditor = () => {
    setIsEditorOpen(false);
    setEditorData(null); // Clear data to ensure fresh state next time
  }

  const handleSaveEditedTimestamps = (key: string, inPoint: number, outPoint: number, lineSpecificGain: number) => {
    console.log(`[VOManager] Saving edited timestamps for key ${key}: In=${inPoint}, Out=${outPoint}, Gain=${lineSpecificGain}dB`);

    // Update voInPoints and voOutPoints (assuming similar logic exists)
    setVoInPoints(prev => ({ ...prev, [key]: inPoint.toString() }));
    setVoOutPoints(prev => ({ ...prev, [key]: outPoint.toString() }));

    // Update lineGainSettings state and persist to localStorage
    setLineGainSettings(prev => {
      const updatedSettings = { ...prev, [key]: lineSpecificGain };
      if (activeSpreadsheetId) { // Ensure activeSpreadsheetId is from the outer scope
        try {
          localStorage.setItem(`lineGainSettings_${activeSpreadsheetId}`, JSON.stringify(updatedSettings));
          console.log(`[VOManager] Saved lineGainSettings to localStorage for ${activeSpreadsheetId}:`, updatedSettings);
        } catch (e) {
          console.error('[VOManager] Error saving lineGainSettings to localStorage:', e);
        }
      }
      return updatedSettings; // Ensure the new state is returned
    });

    // --- RESTORED: Update export settings with line-specific gain for this line only --- START ---
    if (activeSpreadsheetId) {
      // Get existing export settings to preserve global gain and other line-specific gains
      const storageKey = `audioExportSettings_${activeSpreadsheetId}`;
      let existingSettings: any = {};
      
      try {
        const existingSettingsString = localStorage.getItem(storageKey);
        if (existingSettingsString) {
          existingSettings = JSON.parse(existingSettingsString);
        }
      } catch (error) {
        console.error('[VOManager] Error parsing existing export settings:', error);
      }

      // Preserve the global makeupGain, but add/update line-specific gain
      const updatedExportSettings = {
        ...existingSettings,
        makeupGain: existingSettings.makeupGain || String(limiterSettings.gain), // Preserve global gain
        [`lineGain_${key}`]: String(lineSpecificGain) // Add line-specific gain
      };

      try {
        localStorage.setItem(storageKey, JSON.stringify(updatedExportSettings));
        console.log(`[VOManager] Updated export settings with line-specific gain for ${key}: ${lineSpecificGain}dB (${storageKey}):`, updatedExportSettings);
      } catch (error) {
        console.error('[VOManager] Failed to save line-specific export settings:', error);
      }
    } else {
      console.error('[VOManager] Cannot save line-specific export settings: activeSpreadsheetId is missing.');
    }
    // --- RESTORED: Update export settings with line-specific gain for this line only --- END ---

    // Persist to Google Sheet (this is an example, adapt to your existing sheet update logic)
    if (activeSpreadsheetId) {
      const updates = [];
      if ((columnMappings.voInColLetter || detectedColumns.voInCol) && VO_IN_MAP[key]) { // Check if mapping exists
        updates.push({
          range: `${columnMappings.voInColLetter || detectedColumns.voInCol}${VO_IN_MAP[key].substring(1)}`, // Use dynamic col, static row part from MAP
          values: [[inPoint.toFixed(2)]]
        });
      }
      if ((columnMappings.voOutColLetter || detectedColumns.voOutCol) && VO_OUT_MAP[key]) { // Check if mapping exists
        updates.push({
          range: `${columnMappings.voOutColLetter || detectedColumns.voOutCol}${VO_OUT_MAP[key].substring(1)}`,
          values: [[outPoint.toFixed(2)]]
        });
      }
      // If you also save the line-specific gain to the sheet, add it here.
      // Example: updates.push({ range: `P${VO_IN_MAP[key].substring(1)}`, values: [[lineSpecificGain.toString()]] });


      if (updates.length > 0) {
        console.log('[VOManager][handleSaveEditedTimestamps] About to write updates to sheet', {
          spreadsheetId: activeSpreadsheetId,
          updates
        });
        sheetsService.batchUpdateValues(activeSpreadsheetId, updates)
          .then(() => {
            enqueueSnackbar(`Timestamps for ${key} saved to sheet.`, { variant: 'success' });
          })
          .catch(err => {
            console.error(`[VOManager] Error saving timestamps for ${key} to sheet:`, err);
            enqueueSnackbar(`Failed to save timestamps for ${key} to sheet: ${err.message}`, { variant: 'error' });
          });
      }
    }
    setEditorData(null); // Use the correct state setter to close the dialog
  };

  const handlePeaksGenerated = (peaks: number[]) => {
    setProcessedAudioPeaks(peaks);
    // Save peaks to localStorage
    if (activeSpreadsheetId && processedAudioUrl) {
      localStorage.setItem(`processedAudioData_${activeSpreadsheetId}`, JSON.stringify({
        url: processedAudioUrl,
        peaks
      }));
    }
  };

  // Add a clear/reload function
  const reloadAudioData = useCallback((): void => {
    console.log('[VOManager] Forcing audio data reload');
    
    // Clear current audio URL
    setProcessedAudioUrl(null);
    setProcessedAudioPeaks(null);
    
    // If we have an active spreadsheet ID, try to reload from localStorage
    if (activeSpreadsheetId) {
      const storedDataString = localStorage.getItem(`processedAudioData_${activeSpreadsheetId}`);
      console.log(`[VOManager] Checking localStorage for key: processedAudioData_${activeSpreadsheetId}`);
      
      if (storedDataString) {
        try {
          const storedData = JSON.parse(storedDataString);
          console.log('[VOManager] Found stored data:', storedData);
          
          if (storedData.url) {
            // Ensure URL has the API base if it's a relative path
            const fullUrl = storedData.url.startsWith('http') 
              ? storedData.url 
              : `${API_BASE_URL}${storedData.url}`;
            
            console.log(`[VOManager] Setting processedAudioUrl to: ${fullUrl}`);
            setProcessedAudioUrl(fullUrl);
          }
          
          if (storedData.peaks && Array.isArray(storedData.peaks)) {
            setProcessedAudioPeaks(storedData.peaks);
          }
        } catch (e) {
          console.error('[VOManager] Error parsing stored audio data from localStorage', e);
        }
      } else {
        console.log(`[VOManager] No stored audio data found for ${activeSpreadsheetId}`);
      }
    }
  }, [activeSpreadsheetId]);

  // Add this to the useEffect that runs when activeSpreadsheetId changes
  useEffect(() => {
    if (activeSpreadsheetId) {
      fetchVOData();
      // Call the reload function
      reloadAudioData();
    }
  }, [activeSpreadsheetId, fetchVOData, reloadAudioData]);

  // Add a function to handle line audio upload
  const handleLineFileUpload = async (key: string, event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !activeSpreadsheetId) return;

    setIsUploadingLine(key);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('audio', file);
      formData.append('lineKey', key);
      
      console.log(`[VOManager] Uploading file for line ${key}: ${file.name}`);

      const response = await fetch(`${API_BASE_URL}/api/process-line-audio`, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      if (result.fileUrl) {
        // Ensure the URL has the full base URL
        const fullUrl = result.fileUrl.startsWith('http') 
          ? result.fileUrl 
          : `${API_BASE_URL}${result.fileUrl}`;
        
        console.log(`[VOManager] Setting line audio URL for ${key} to: ${fullUrl}`);
        setLineAudioFiles(prev => ({ ...prev, [key]: fullUrl }));
        
        // Save to localStorage
        const lineAudioStorage = JSON.parse(localStorage.getItem(`lineAudioFiles_${activeSpreadsheetId}`) || '{}');
        lineAudioStorage[key] = fullUrl;
        localStorage.setItem(`lineAudioFiles_${activeSpreadsheetId}`, JSON.stringify(lineAudioStorage));
      }
    } catch (err) {
      console.error(`Error uploading file for line ${key}:`, err);
      setError(`Error uploading file for line ${key}: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setIsUploadingLine(null);
    }
  };

  // Add useEffect to load line audio files from localStorage
  useEffect(() => {
    if (activeSpreadsheetId) {
      try {
        const lineAudioStorage = localStorage.getItem(`lineAudioFiles_${activeSpreadsheetId}`);
        if (lineAudioStorage) {
          const lineAudios = JSON.parse(lineAudioStorage);
          setLineAudioFiles(lineAudios);
          console.log(`[VOManager] Loaded line audio files from localStorage for ${activeSpreadsheetId}:`, lineAudios);
        } else {
          console.log(`[VOManager] No line audio files found in localStorage for ${activeSpreadsheetId}`);
        }
      } catch (error) {
        console.error(`[VOManager] Error loading line audio files from localStorage:`, error);
      }
    }
  }, [activeSpreadsheetId]);

  if (!activeSpreadsheetId) {
    return (
       <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', mt: 4, p: 2 }}>
         <Typography variant="h6" sx={{ mb: 2 }}>No Chart Date Selected</Typography>
         <Typography variant="body1" sx={{ mb: 2 }}>Please select a date on the Charts page first.</Typography>
         <Button 
           onClick={() => navigate('/')} 
           variant="contained"
           sx={{ 
             textTransform: 'none',
             fontWeight: 600
           }}
         >
           Go to Charts
         </Button>
       </Box>
    );
  }

  if (loading) {
     return (
       <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
         <CircularProgress />
       </Box>
     );
   }

  return (
    <Box sx={{ maxWidth: 800, margin: 'auto', mt: 4, p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="body1" sx={{ fontWeight: 400 }}>Upload VO</Typography>
          <FormControlLabel
            control={
              <Switch
                checked={isGenerateMode}
                onChange={(e) => setIsGenerateMode(e.target.checked)}
                color="primary"
              />
            }
            label=""
          />
          <Typography variant="body1" sx={{ fontWeight: 400 }}>Generate VO</Typography>
        </Stack>
        
        {/* Rest of the top buttons */}
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {isGenerateMode && (
            <>
              <Tooltip title="Generate Audio for All Entries Missing It">
                <span>
                  <IconButton
                    onClick={handleGenerateAll}
                    disabled={isGeneratingAll || loading}
                    color="primary"
                  >
                    {isGeneratingAll ? <CircularProgress size={24} /> : <AutoFixHighIcon />}
                  </IconButton>
                </span>
              </Tooltip>

              <Tooltip title="Voice Settings">
                <IconButton onClick={() => setIsSettingsOpen(true)} color="primary" disabled={isGeneratingAll}>
                  <SettingsIcon />
                </IconButton>
              </Tooltip>
            </>
          )}

          <Tooltip title="Global Gain Settings">
            <IconButton onClick={() => setShowLimiterDialog(true)} color="primary" disabled={isGeneratingAll}>
              <SettingsIcon />
            </IconButton>
          </Tooltip>

          <Button
            variant="outlined" // Changed from contained
            onClick={() => setScriptDialogOpen(true)}
            disabled={isGeneratingAll}
            sx={{ 
              textTransform: 'none',
              // fontWeight: 600 // Removed to match preferred style
            }}
          >
            Upload Script
          </Button>

          <Button
            variant="outlined" // Changed from contained
            onClick={() => navigate('/')}
            disabled={isGeneratingAll}
            sx={{ 
              textTransform: 'none',
              // fontWeight: 600 // Removed to match preferred style
            }}
          >
            Charts
          </Button>
          
          {/* Add Export Button */}
          {/* <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Button
              variant="outlined" // Changed from contained
              onClick={() => setScriptDialogOpen(true)} // Use the export all handler
              disabled={isGeneratingAll || (!isGenerateMode && !processedAudioUrl)}
              sx={{
                textTransform: 'none',
              }}
            >
              'Export All Clips'
            </Button>
          </Box> */}
        </Box>
      </Box>

      {/* Add back the error message alert */}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* Display the main VU meter permanently instead of conditionally */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>
          Audio Level {playingKey ? `- ${playingKey}` : ''}
        </Typography>
        <VUMeter 
          analyser={activeAnalyser} 
          isPlaying={!!playingKey} 
          width={300}
          height={24}
        />
      </Box>

      {isGenerateMode ? (
        <TableContainer
          component={Paper}
          sx={{
            border: '1px solid rgba(224, 224, 224, 1)',
            borderRadius: '4px',
          }}
        >
          <Table stickyHeader aria-label="vo manager table" size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: '100px', textAlign: 'center' }}>Controls</TableCell>
                {!isGenerateMode && (
                  <>
                    <TableCell sx={{ width: '80px', textAlign: 'center' }}>VO In</TableCell>
                    <TableCell sx={{ width: '80px', textAlign: 'center' }}>VO Out</TableCell>
                  </>
                )}
                <TableCell sx={{ width: '40px' }}>Position</TableCell>
                <TableCell>Script</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {DISPLAY_ORDER.map((key) => (
                <TableRow
                  key={key}
                  hover
                  sx={{
                    '& > td, & > th': { // Target both td and th
                      height: '60px', // Apply fixed height
                      padding: '8px 12px',
                      // verticalAlign: 'top', // Ensure this line is removed or commented out if present
                    }
                  }}
                >
                  <TableCell sx={{ width: '100px', textAlign: 'center', borderRight: '1px solid rgba(224, 224, 224, 1)' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                        {/* Add Upload Button for per-line upload */}
                        <Tooltip title={`Upload audio for line ${key}`}>
                          <span>
                            <IconButton
                              component="label"
                              size="small"
                              color={lineAudioFiles[key] ? 'success' : 'primary'}
                              sx={{ padding: '4px' }}
                              disabled={isGeneratingAll || isProcessing || isUploadingLine === key}
                            >
                              {isUploadingLine === key ? (
                                <CircularProgress size={16} />
                              ) : (
                                <UploadIcon />
                              )}
                              <VisuallyHiddenInput
                                type="file"
                                accept="audio/*"
                                onChange={(e) => handleLineFileUpload(key, e)}
                              />
                            </IconButton>
                          </span>
                        </Tooltip>
                        
                        <Tooltip title="Edit in Waveform Editor">
                          <span>
                            <IconButton
                              size="small"
                              color="primary"
                              sx={{ padding: '4px' }}
                              onClick={() => handleOpenWaveformEditor(key)}
                              disabled={ // Update this disabled logic
                                !( // Enable if EITHER default outro OR standard case with data
                                  (key === 'Outro' && (!voData[key] || voData[key].trim() === '')) || // Case 1: Is default Outro
                                  ( // Case 2: Standard case
                                    (lineAudioFiles[key] || processedAudioUrl) && // Needs an audio URL (line or main)
                                    voInPoints[key] && voOutPoints[key] && // Needs points
                                    !isNaN(parseFloat(voInPoints[key])) && !isNaN(parseFloat(voOutPoints[key])) // Points must be valid numbers
                                  )
                                )
                              }
                            >
                              <GraphicEqIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                        
                        <Tooltip 
                          title={
                            playingKey === key 
                              ? "Stop Audio" 
                              : key === 'Outro' && (
                                (!voData[key] || voData[key].trim() === '') || 
                                (voInPoints[key] === '0' && voOutPoints[key] === '0')
                              )
                                ? "Play Default Outro (used when script is empty or in/out points are both 0)" 
                                : (!voInPoints[key] || !voOutPoints[key])
                                  ? "Missing in/out points" 
                                  : "Play Audio"
                          }
                        >
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => handleVOButtonClick(key)}
                              disabled={isGeneratingAll || ((!voInPoints[key] || !voOutPoints[key]) && key !== 'Outro')}
                              color={playingKey === key ? 'secondary' : 'primary'}
                              sx={{ padding: '4px' }}
                            >
                              {playingKey === key ? <StopIcon /> : <PlayArrowIcon />}
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                      
                      {/* Small VU meter that appears in each row */}
                      <VUMeter 
                        analyser={playingKey === key ? activeAnalyser : null}
                        isPlaying={playingKey === key}
                        width={80}
                        height={16}
                      />
                    </Box>
                  </TableCell>

                  {!isGenerateMode && (
                    <>
                      <TableCell
                        onClick={() => handleInPointClick(key)}
                        sx={{
                          width: '80px',
                          borderRight: '1px solid rgba(224, 224, 224, 1)',
                          textAlign: 'center',
                          verticalAlign: 'middle', // Add vertical alignment
                        }}
                      >
                        {editingCell?.key === `in_${key}` ? (
                          <TextField
                            value={editingCell.value}
                            onChange={(e) => handleCellEditChange(`in_${key}`, e.target.value)}
                            onBlur={(e) => handleInPointSave(key, e.target.value)}
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                handleInPointSave(key, (e.target as HTMLInputElement).value);
                              } else if (e.key === 'Escape') {
                                setEditingCell(null);
                              }
                            }}
                            placeholder="IN"
                            size="small"
                            variant="outlined"
                            autoFocus
                            sx={{
                              width: '70px',
                              '& .MuiInputBase-root': {
                                padding: '2px 4px',
                              }
                            }}
                          />
                        ) : (
                          <Typography variant="body2" sx={{ color: voInPoints[key] ? 'inherit' : 'text.disabled', cursor: 'pointer' }}>
                            {/* Display default 0.00 for empty Outro, otherwise state value */}
                            {(key === 'Outro' && (!voData[key] || voData[key].trim() === '')) 
                              ? '0.00' 
                              : (typeof voInPoints[key] === 'number' || (typeof voInPoints[key] === 'string' && !isNaN(parseFloat(voInPoints[key])))) 
                              ? parseFloat(voInPoints[key].toString()).toFixed(2) 
                                : (voInPoints[key] || 'IN')}
                        </Typography>
                        )}
                      </TableCell>

                      <TableCell
                        onClick={() => handleOutPointClick(key)}
                        sx={{
                          width: '80px',
                          borderRight: '1px solid rgba(224, 224, 224, 1)',
                          textAlign: 'center',
                          verticalAlign: 'middle', // Add vertical alignment
                        }}
                      >
                        {editingCell?.key === `out_${key}` ? (
                          <TextField
                            value={editingCell.value}
                            onChange={(e) => handleCellEditChange(`out_${key}`, e.target.value)}
                            onBlur={(e) => handleOutPointSave(key, e.target.value)}
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                handleOutPointSave(key, (e.target as HTMLInputElement).value);
                              } else if (e.key === 'Escape') {
                                setEditingCell(null);
                              }
                            }}
                            placeholder="OUT"
                            size="small"
                            variant="outlined"
                            autoFocus
                            sx={{
                              width: '70px',
                              '& .MuiInputBase-root': {
                                padding: '2px 4px',
                              }
                            }}
                          />
                        ) : (
                          <Typography variant="body2" sx={{ color: voOutPoints[key] ? 'inherit' : 'text.disabled', cursor: 'pointer' }}>
                            {/* Display default 7.00 for empty Outro, otherwise state value */}
                            {(key === 'Outro' && (!voData[key] || voData[key].trim() === ''))
                             ? '7.00'
                             : (typeof voOutPoints[key] === 'number' || (typeof voOutPoints[key] === 'string' && !isNaN(parseFloat(voOutPoints[key]))))
                              ? parseFloat(voOutPoints[key].toString()).toFixed(2) 
                               : (voOutPoints[key] || 'OUT')}
                        </Typography>
                        )}
                      </TableCell>
                    </>
                  )}

                  <TableCell
                    component="th"
                    scope="row"
                    sx={{ 
                      width: '40px', 
                      fontWeight: 'bold', 
                      borderRight: '1px solid rgba(224, 224, 224, 1)', 
                      textAlign: 'center',
                      fontSize: '1.25rem', // Increase font size by 25%
                      verticalAlign: 'middle' // Add vertical alignment
                    }}
                  >
                    {key}
                  </TableCell>

                  <TableCell
                    onClick={() => !editingCell && handleCellClick(key)}
                    sx={{
                      cursor: isGeneratingAll ? 'default' : 'pointer',
                      minWidth: '300px',
                      whiteSpace: 'pre-wrap',
                      verticalAlign: 'middle' // Add vertical alignment
                    }}
                  >
                    {editingCell?.key === key ? (
                      <TextField
                        value={editingCell.value}
                        onChange={(e) => handleCellEditChange(key, e.target.value)}
                        onBlur={(e) => handleSave(key, e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            handleSave(key, (e.target as HTMLInputElement).value);
                          } else if (e.key === 'Escape') {
                            setEditingCell(null);
                          }
                        }}
                        multiline
                        fullWidth
                        size="small"
                        variant="outlined"
                        autoFocus
                        placeholder={key === 'Outro' ? DEFAULT_OUTRO_TEXT : undefined}
                        sx={{
                          padding: 0,
                          '& .MuiInputBase-root': {
                            padding: '6px 8px',
                          },
                          '& .MuiInputBase-input::placeholder': {
                            color: '#999',
                            fontStyle: 'italic'
                          }
                        }}
                      />
                    ) : (
                      <Typography variant="body2" sx={{ 
                        color: voData[key] ? 'inherit' : 'text.disabled', 
                        minHeight: '20px',
                        fontStyle: key === 'Outro' && !voData[key] ? 'italic' : 'normal'
                      }}>
                        {voData[key] || (key === 'Outro' ? DEFAULT_OUTRO_TEXT : '-')}
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Box sx={{ mt: 2 }}>
          <Button
            component="label"
            variant="contained"
            startIcon={<CloudUploadIcon />}
            disabled={isProcessing}
          >
            {isProcessing ? 'Processing...' : 'Upload Audio File'}
            <VisuallyHiddenInput
              type="file"
              accept="audio/*"
              onChange={handleFileUpload}
            />
          </Button>
          
          <TableContainer
            component={Paper}
            sx={{
              border: '1px solid rgba(224, 224, 224, 1)',
              borderRadius: '4px',
            }}
          >
            <Table stickyHeader aria-label="vo manager table" size="small">
              <TableBody>
                {DISPLAY_ORDER.map((key) => (
                  <TableRow
                    key={key}
                    hover
                    sx={{
                      '& > td, & > th': { // Target both td and th
                        height: '60px', // Apply fixed height
                        padding: '8px 12px',
                        // verticalAlign: 'top', // Ensure this line is removed or commented out if present
                      }
                    }}
                  >
                    <TableCell sx={{ width: '100px', textAlign: 'center', borderRight: '1px solid rgba(224, 224, 224, 1)' }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                        <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                          {/* Add Upload Button for per-line upload in non-generate mode too */}
                          <Tooltip title={`Upload audio for line ${key}`}>
                            <span>
                              <IconButton
                                component="label"
                                size="small"
                                color={lineAudioFiles[key] ? 'success' : 'primary'}
                                sx={{ padding: '4px' }}
                                disabled={isGeneratingAll || isProcessing || isUploadingLine === key}
                              >
                                {isUploadingLine === key ? (
                                  <CircularProgress size={16} />
                                ) : (
                                  <UploadIcon />
                                )}
                                <VisuallyHiddenInput
                                  type="file"
                                  accept="audio/*"
                                  onChange={(e) => handleLineFileUpload(key, e)}
                                />
                              </IconButton>
                            </span>
                          </Tooltip>
                        
                          <Tooltip title="Edit in Waveform Editor">
                            <span>
                              <IconButton
                                size="small"
                                color="primary"
                                sx={{ padding: '4px' }}
                                onClick={() => handleOpenWaveformEditor(key)}
                                disabled={ // Update this disabled logic
                                  !( // Enable if EITHER default outro OR standard case with data
                                    (key === 'Outro' && (!voData[key] || voData[key].trim() === '')) || // Case 1: Is default Outro
                                    ( // Case 2: Standard case
                                      (lineAudioFiles[key] || processedAudioUrl) && // Needs an audio URL (line or main)
                                      voInPoints[key] && voOutPoints[key] && // Needs points
                                      !isNaN(parseFloat(voInPoints[key])) && !isNaN(parseFloat(voOutPoints[key])) // Points must be valid numbers
                                    )
                                  )
                                }
                              >
                                <GraphicEqIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                          
                          <Tooltip 
                            title={
                              playingKey === key 
                                ? "Stop Audio" 
                                : key === 'Outro' && (
                                  (!voData[key] || voData[key].trim() === '') || 
                                  (voInPoints[key] === '0' && voOutPoints[key] === '0')
                                )
                                  ? "Play Default Outro (used when script is empty or in/out points are both 0)" 
                                  : (!voInPoints[key] || !voOutPoints[key])
                                    ? "Missing in/out points" 
                                    : "Play Audio"
                            }
                          >
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => handleVOButtonClick(key)}
                                disabled={isGeneratingAll || ((!voInPoints[key] || !voOutPoints[key]) && key !== 'Outro')}
                                color={playingKey === key ? 'secondary' : 'primary'}
                                sx={{ padding: '4px' }}
                              >
                                {playingKey === key ? <StopIcon /> : <PlayArrowIcon />}
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                        
                        {/* Small VU meter that appears in each row */}
                        <VUMeter 
                          analyser={playingKey === key ? activeAnalyser : null}
                          isPlaying={playingKey === key}
                          width={80}
                          height={16}
                        />
                      </Box>
                    </TableCell>

                    {!isGenerateMode && (
                      <>
                        <TableCell
                          onClick={() => handleInPointClick(key)}
                          sx={{
                            width: '80px',
                            borderRight: '1px solid rgba(224, 224, 224, 1)',
                            textAlign: 'center',
                            verticalAlign: 'middle', // Add vertical alignment
                          }}
                        >
                          {editingCell?.key === `in_${key}` ? (
                            <TextField
                              value={editingCell.value}
                              onChange={(e) => handleCellEditChange(`in_${key}`, e.target.value)}
                              onBlur={(e) => handleInPointSave(key, e.target.value)}
                              onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  handleInPointSave(key, (e.target as HTMLInputElement).value);
                                } else if (e.key === 'Escape') {
                                  setEditingCell(null);
                                }
                              }}
                              placeholder="IN"
                              size="small"
                              variant="outlined"
                              autoFocus
                              sx={{
                                width: '70px',
                                '& .MuiInputBase-root': {
                                  padding: '2px 4px',
                                }
                              }}
                            />
                          ) : (
                            <Typography variant="body2" sx={{ color: voInPoints[key] ? 'inherit' : 'text.disabled', cursor: 'pointer' }}>
                              {/* Display default 0.00 for empty Outro, otherwise state value */}
                              {(key === 'Outro' && (!voData[key] || voData[key].trim() === '')) 
                                ? '0.00' 
                                : (typeof voInPoints[key] === 'number' || (typeof voInPoints[key] === 'string' && !isNaN(parseFloat(voInPoints[key])))) 
                                ? parseFloat(voInPoints[key].toString()).toFixed(2) 
                                  : (voInPoints[key] || 'IN')}
                          </Typography>
                          )}
                        </TableCell>

                        <TableCell
                          onClick={() => handleOutPointClick(key)}
                          sx={{
                            width: '80px',
                            borderRight: '1px solid rgba(224, 224, 224, 1)',
                            textAlign: 'center',
                            verticalAlign: 'middle', // Add vertical alignment
                          }}
                        >
                          {editingCell?.key === `out_${key}` ? (
                            <TextField
                              value={editingCell.value}
                              onChange={(e) => handleCellEditChange(`out_${key}`, e.target.value)}
                              onBlur={(e) => handleOutPointSave(key, e.target.value)}
                              onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  handleOutPointSave(key, (e.target as HTMLInputElement).value);
                                } else if (e.key === 'Escape') {
                                  setEditingCell(null);
                                }
                              }}
                              placeholder="OUT"
                              size="small"
                              variant="outlined"
                              autoFocus
                              sx={{
                                width: '70px',
                                '& .MuiInputBase-root': {
                                  padding: '2px 4px',
                                }
                              }}
                            />
                          ) : (
                            <Typography variant="body2" sx={{ color: voOutPoints[key] ? 'inherit' : 'text.disabled', cursor: 'pointer' }}>
                              {/* Display default 7.00 for empty Outro, otherwise state value */}
                              {(key === 'Outro' && (!voData[key] || voData[key].trim() === ''))
                               ? '7.00'
                               : (typeof voOutPoints[key] === 'number' || (typeof voOutPoints[key] === 'string' && !isNaN(parseFloat(voOutPoints[key]))))
                                ? parseFloat(voOutPoints[key].toString()).toFixed(2) 
                                 : (voOutPoints[key] || 'OUT')}
                          </Typography>
                          )}
                        </TableCell>
                      </>
                    )}

                    <TableCell
                      component="th"
                      scope="row"
                      sx={{ 
                        width: '40px', 
                        fontWeight: 'bold', 
                        borderRight: '1px solid rgba(224, 224, 224, 1)', 
                        textAlign: 'center',
                        fontSize: '1.25rem', // Increase font size by 25%
                        verticalAlign: 'middle' // Add vertical alignment
                      }}
                    >
                      {key}
                    </TableCell>

                    <TableCell
                      onClick={() => !editingCell && handleCellClick(key)}
                      sx={{
                        cursor: isGeneratingAll ? 'default' : 'pointer',
                        minWidth: '300px',
                        whiteSpace: 'pre-wrap',
                        verticalAlign: 'middle' // Add vertical alignment
                      }}
                    >
                      {editingCell?.key === key ? (
                        <TextField
                          value={editingCell.value}
                          onChange={(e) => handleCellEditChange(key, e.target.value)}
                          onBlur={(e) => handleSave(key, e.target.value)}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                              e.preventDefault();
                              handleSave(key, (e.target as HTMLInputElement).value);
                            } else if (e.key === 'Escape') {
                              setEditingCell(null);
                            }
                          }}
                          multiline
                          fullWidth
                          size="small"
                          variant="outlined"
                          autoFocus
                          placeholder={key === 'Outro' ? DEFAULT_OUTRO_TEXT : undefined}
                          sx={{
                            padding: 0,
                            '& .MuiInputBase-root': {
                              padding: '6px 8px',
                            },
                            '& .MuiInputBase-input::placeholder': {
                              color: '#999',
                              fontStyle: 'italic'
                            }
                          }}
                        />
                      ) : (
                        <Typography variant="body2" sx={{ 
                          color: voData[key] ? 'inherit' : 'text.disabled', 
                          minHeight: '20px',
                          fontStyle: key === 'Outro' && !voData[key] ? 'italic' : 'normal'
                        }}>
                          {voData[key] || (key === 'Outro' ? DEFAULT_OUTRO_TEXT : '-')}
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      <SettingsDialog
        open={isSettingsOpen}
        onClose={() => setIsSettingsOpen(false)}
        currentSettings={voiceSettings}
        onSave={handleSaveSettings}
      />

      <ScriptManager
        open={scriptDialogOpen}
        onClose={() => setScriptDialogOpen(false)}
        onSave={handleScriptSave}
      />

      <LimiterSettingsDialog
        open={showLimiterDialog}
        onClose={() => setShowLimiterDialog(false)}
        onSave={handleLimiterChange}
        initialSettings={limiterSettings}
      />

      {/* Progress indicator */}
      {isProcessing && (
        <Box sx={{ width: '100%', mt: 2 }}>
          <LinearProgress variant="determinate" value={uploadProgress} />
          <Typography variant="body2" color="text.secondary" align="center">
            {uploadProgress}%
          </Typography>
        </Box>
      )}

      {/* Transcription result */}
      {isUploadSuccess && transcriptionResult && (
        <Box sx={{ mt: 3, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
          <Typography variant="h6" gutterBottom>
            Transcription Result
          </Typography>
          <Typography variant="body1">
            {transcriptionResult.text}
          </Typography>
        </Box>
      )}

      {/* STT Word-level timestamps */}
      {transcriptionResult && transcriptionResult.words && (
        <Box sx={{ mt: 3, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
          <Typography variant="h6" gutterBottom>
            STT Word-level timestamps {transcriptionResult.words.length} words
          </Typography>
          <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>Word</th>
                  <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>Start</th>
                  <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>End</th>
                </tr>
              </thead>
              <tbody>
                {transcriptionResult.words.map((word, index) => {
                  // Convert to numbers and ensure they are valid
                  const startTime = typeof word.startTime === 'string' ? parseFloat(word.startTime) : 
                    typeof word.startTime === 'number' ? word.startTime : 
                    ((word.startTime as any)?.seconds || 0) + ((word.startTime as any)?.nanos || 0) / 1e9;
                  
                  const endTime = typeof word.endTime === 'string' ? parseFloat(word.endTime) : 
                    typeof word.endTime === 'number' ? word.endTime : 
                    ((word.endTime as any)?.seconds || 0) + ((word.endTime as any)?.nanos || 0) / 1e9;
                  
                  return (
                    <tr key={index}>
                      <td style={{ border: '1px solid #ddd', padding: '8px' }}>{word.word}</td>
                      <td style={{ border: '1px solid #ddd', padding: '8px' }}>{!isNaN(startTime) ? startTime.toFixed(2) : 'N/A'}s</td>
                      <td style={{ border: '1px solid #ddd', padding: '8px' }}>{!isNaN(endTime) ? endTime.toFixed(2) : 'N/A'}s</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Box>
          <Button 
            onClick={() => console.log('TranscriptionResult:', transcriptionResult)} 
            variant="outlined" 
            size="small" 
            sx={{ mt: 1 }}
          >
            Debug
          </Button>
        </Box>
      )}

      {/* Final timestamps */}
      {transcriptionResult && transcriptionResult.timestamps && Object.keys(transcriptionResult.timestamps).length > 0 && (
        <Box sx={{ mt: 3, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
          <Typography variant="h6" gutterBottom>
            Gemini-processed Timestamps
          </Typography>
          <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', padding: '8px' }}>Line</th>
                  <th style={{ textAlign: 'left', padding: '8px' }}>In Point</th>
                  <th style={{ textAlign: 'left', padding: '8px' }}>Out Point</th>
                  <th style={{ textAlign: 'left', padding: '8px' }}>Duration</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(transcriptionResult.timestamps).map(([key, value]) => {
                  // Convert to numbers and ensure they are valid
                  const inPoint = typeof value.inPoint === 'string' ? parseFloat(value.inPoint) : value.inPoint;
                  const outPoint = typeof value.outPoint === 'string' ? parseFloat(value.outPoint) : value.outPoint;
                  
                  if (isNaN(inPoint) || isNaN(outPoint)) return null;
                  
                  return (
                    <tr key={key} style={{ borderBottom: '1px solid #ddd' }}>
                      <td style={{ padding: '8px' }}>{key}</td>
                      <td style={{ padding: '8px' }}>{inPoint.toFixed(2)}s</td>
                      <td style={{ padding: '8px' }}>{outPoint.toFixed(2)}s</td>
                      <td style={{ padding: '8px' }}>{(outPoint - inPoint).toFixed(2)}s</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Box>
        </Box>
      )}

      {/* Waveform Editor Dialog */}
      {isEditorOpen && editorData && (
        <WaveformEditorDialog
          open={isEditorOpen}
          onClose={handleCloseEditor} // Use the new handler
          onSave={handleSaveEditedTimestamps}
          audioUrl={editorData.audioUrl ?? ''} // Provide fallback
          initialInPoint={editorData.initialInPoint ?? 0} // Provide fallback
          initialOutPoint={editorData.initialOutPoint ?? 0} // Provide fallback
          initialGain={editorData.initialGain ?? 0} // Read from editorData, provide fallback
          lineKey={editorData.lineKey} // Use lineKey (already correct here)
          songTitle={editorData.songTitle} // Keep as is (optional in props)
          songArtist={editorData.songArtist} // Keep as is (optional in props)
          peaks={processedAudioPeaks ?? undefined} // Convert null to undefined
          onPeaksGenerated={handlePeaksGenerated} // Pass down the handler
        />
      )}

      {/* Add a refresh button */}
      <Button
        variant="outlined"
        onClick={reloadAudioData}
        disabled={isProcessing}
        sx={{ ml: 1 }}
      >
        Refresh Audio
      </Button>
    </Box>
  );
};

export default VOManager; 